import { htmlSafe } from "./html";
import { AbsenceMode, BonusCompType, employmentTypeLabel, NominalHoursMode, VacationIncrement, } from "./model";
import { findPositionById, formatNumber, isDateInRange, toDurationString } from "./utils";
// Renders the worked days of a fixed-day contract as "<weekday> <hours>" fragments. The index
// is paired before filtering so the weekday label stays correct when earlier days are zero.
export const formatFixedDayHours = (hoursPerDay) => hoursPerDay
    ?.map((h, i) => [h, i])
    .filter(([h]) => !!h)
    .map(([h, i]) => htmlSafe `
                ${["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"][i]}
                <strong> ${toDurationString(h, true)} </strong>
            `);
const FIELDS = [
    {
        prop: "employmentType",
        label: "Beschäftigungsverhältnis",
        formatValue: (_, val) => htmlSafe `${employmentTypeLabel(val)}`,
    },
    {
        prop: ["nominalHoursMode", "hoursPerWeek", "hoursPerDay"],
        label: "Sollstunden",
        formatValue: (_, mode, hoursPerWeek, hoursPerDay) => {
            const fixedDays = formatFixedDayHours(hoursPerDay);
            switch (mode) {
                case NominalHoursMode.WeekFactor:
                    return htmlSafe `${formatNumber(hoursPerWeek)} Std / Wo (Wochenfaktor)`;
                case NominalHoursMode.Exact:
                    return htmlSafe `${formatNumber(hoursPerWeek)} Std / Wo (Monatsgenau)`;
                case NominalHoursMode.ExactWithoutHolidays:
                    return htmlSafe `${formatNumber(hoursPerWeek)} Std / Wo (Monatsgen. abz. Feiert.)`;
                case NominalHoursMode.ExactWithoutHolidaysUsingAbsenceHours:
                    return htmlSafe `${formatNumber(hoursPerWeek)} Std / Wo (Monatsgen. abz. Feiert. mit Fehlstd.)`;
                case NominalHoursMode.FixedDays:
                    return fixedDays?.length ? htmlSafe `${fixedDays}` : htmlSafe ``;
                case NominalHoursMode.FixedDaysWithoutHolidays:
                    return htmlSafe `${fixedDays} (abz. Feiert.)`;
            }
        },
    },
    {
        prop: "salaries",
        label: "Lohn/Gehalt",
        formatValue: (company, salaries) => htmlSafe `${salaries
            .map((salary) => {
            const comission = salary.commission ? ` + ${formatNumber(salary.commission)}% Provision` : "";
            const position = salary.positionId
                ? findPositionById([...company.venues, ...company.archivedVenues], salary.positionId)?.position
                : null;
            const positionName = !salary.positionId || !position ? "" : ` (${position.name})`;
            return `${formatNumber(salary.amount)} € / ${salary.type === "monthly" ? "Monat" : "Stunde"}${comission}${positionName}`;
        })
            .join(", ")}`,
    },
    {
        prop: "benefits",
        label: "Sonderleistungen",
        formatValue: (company, val) => htmlSafe `${val
            .map(({ typeId, amount }) => {
            const type = company.benefitTypes.find((t) => t.id === typeId);
            return `${type?.name || "Unbekannt"}: ${formatNumber(amount)} €`;
        })
            .join(", ") || "Keine"}`,
    },
    {
        prop: "bonuses",
        label: "Zuschläge",
        formatValue: (company, val) => htmlSafe `${val
            .sort((a, b) => a.id - b.id)
            .map(({ typeId, percent, hourlyRate, fixedAmount, positionId }) => {
            const type = company.bonusTypes.find((t) => t.id === typeId);
            const typeName = type?.name || "Unbekannt";
            const position = positionId
                ? findPositionById([...company.venues, ...company.archivedVenues], positionId)?.position
                : null;
            const positionName = !positionId || !position ? "" : ` (${position.name})`;
            const name = `${typeName}${positionName}`;
            const compType = type?.compType || BonusCompType.ModifiedHourlyRate;
            if (compType === BonusCompType.FixedAmount) {
                return `${name}: ${formatNumber(fixedAmount || 0)} € Festbetrag`;
            }
            else if (compType === BonusCompType.FixedHourlyRate) {
                return `${name}: ${formatNumber(hourlyRate || 0)} € / Stunde`;
            }
            else {
                return `${name}: ${formatNumber(percent)} %`;
            }
        })
            .join(", ") || "Keine"}`,
    },
    {
        prop: ["absenceMode", "absenceHours", "hoursPerDay"],
        label: "Fehlstundenberechnung",
        formatValue: (_, val, hours, hoursPerDay) => {
            if (val === AbsenceMode.Average) {
                return htmlSafe `13-Wochen-Durchschnitt`;
            }
            if (val === AbsenceMode.FixedDays) {
                const fixedDays = formatFixedDayHours(hoursPerDay);
                return htmlSafe `Feste Wochentage: ${fixedDays?.length ? fixedDays : "Keine"}`;
            }
            return htmlSafe `Pauschale Stundenzahl: ${formatNumber(hours)} Stunden`;
        },
        hideOnAccountingReport: true,
    },
    {
        prop: "annualTimeSheet",
        label: "Arbeitszeitkonto vereinbart",
        formatValue: (_, val) => (val ? htmlSafe `Ja` : htmlSafe `Nein`),
        hideOnAccountingReport: true,
    },
    {
        prop: "enableSFNLedger",
        label: "Zuschlagskonto",
        formatValue: (_, val) => (val ? htmlSafe `Ja` : htmlSafe `Nein`),
        hideOnAccountingReport: true,
    },
    {
        prop: "sfnAdvance",
        label: "SFN-Pauschale",
        formatValue: (_, val) => htmlSafe `${formatNumber(val)} € / Monat`,
    },
    {
        prop: "maxSalary",
        label: "Lohngrenze",
        formatValue: (_, val) => (val ? htmlSafe `${formatNumber(val)} €` : htmlSafe `Keine`),
    },
    {
        prop: "vacationDays",
        label: "Urlaubsanspruch",
        formatValue: (_, val) => htmlSafe `${formatNumber(val)} Tage / Jahr`,
    },
    {
        prop: "vacationIncrement",
        label: "Urlaubszuwachs",
        formatValue: (_, val) => val === VacationIncrement.Monthly ? htmlSafe `monatlich` : htmlSafe `nach Arbeitsstunden`,
        hideOnAccountingReport: true,
    },
    // {
    //     prop: "bonusNight1",
    //     label: "Nacht-1-Zuschlag",
    //     formatValue: (val: number) => `${formatNumber(val)} %`,
    // },
    // {
    //     prop: "bonusNight2",
    //     label: "Nacht-2-Zuschlag",
    //     formatValue: (val: number) => `${formatNumber(val)} %`,
    // },
    // {
    //     prop: "bonusSunday",
    //     label: "Sonntagszuschlag",
    //     formatValue: (val: number) => `${formatNumber(val)} %`,
    // },
    // { prop: "bonusHoliday", label: "Feitertagszuschlag", formatValue: (val: number) => `${formatNumber(val)} %` },
    // { prop: "bonusSpecial", label: "Sondertags-Zuschlag", formatValue: (val: number) => `${formatNumber(val)} %` },
    // {
    //     prop: "stackBonuses",
    //     label: "Zuschläge kumulieren",
    //     formatValue: (val: boolean) => (val ? "Ja" : "Nein"),
    // },
    {
        prop: "blocked",
        label: "Beschäftigungspause",
        formatValue: (_, val) => (val ? htmlSafe `Ja` : htmlSafe `Nein`),
    },
    {
        prop: "probationInclusiveEnd",
        label: "Probezeit",
        formatValue: (_, val) => {
            if (!val)
                return htmlSafe `Keine`;
            return htmlSafe `bis ${new Date(val).toLocaleDateString("de-DE", {
                year: "numeric",
                month: "2-digit",
                day: "2-digit",
            })}`;
        },
    },
    {
        prop: "inclusiveEnd",
        label: "Vertragsende",
        formatValue: (_, val) => {
            if (!val)
                return htmlSafe `Unbefristet`;
            return htmlSafe `${new Date(val).toLocaleDateString("de-DE", {
                year: "numeric",
                month: "2-digit",
                day: "2-digit",
            })}`;
        },
    },
];
export function compareContracts(company, a, b) {
    const change = {
        date: a.end,
        type: b.blocked ? "pause" : a.blocked && !b.blocked ? "resume" : "change",
        fields: [],
        contractA: a,
        contractB: b,
    };
    for (const { prop, label, formatValue, hideOnAccountingReport } of FIELDS) {
        const before = Array.isArray(prop)
            ? formatValue(company, ...prop.map((p) => a[p]))
            : formatValue(company, a[prop]);
        const after = Array.isArray(prop)
            ? formatValue(company, ...prop.map((p) => b[p]))
            : formatValue(company, b[prop]);
        // SafeHtml values compare by reference, so compare rendered strings
        if (String(before) !== String(after)) {
            change.fields.push({
                label,
                before,
                after,
                hideOnAccountingReport,
            });
        }
    }
    return change;
}
export function getContractChanges(company, emp, range) {
    const contracts = [...emp.contracts].sort((a, b) => (a.start < b.start ? -1 : 1));
    const changes = [];
    const inRange = (date) => !range || isDateInRange(date, range);
    const startEvent = (contract) => ({
        type: "start",
        date: contract.start,
        contractB: contract,
        fields: FIELDS.map(({ label, prop, formatValue, hideOnAccountingReport }) => ({
            hideOnAccountingReport,
            label,
            after: Array.isArray(prop)
                ? formatValue(company, ...prop.map((p) => contract[p]))
                : formatValue(company, contract[prop]),
        })),
    });
    // The first contract always begins with a "start"; every later start is emitted below only
    // when a boundary is not a contiguous change.
    const first = contracts[0];
    if (first && inRange(first.start)) {
        changes.push(startEvent(first));
    }
    for (let i = 0; i < contracts.length; i++) {
        const curr = contracts[i];
        const next = contracts[i + 1];
        if (next && curr.end === next.start) {
            // contiguous contracts, compare them if the next one starts in range
            if (inRange(next.start)) {
                changes.push(compareContracts(company, curr, next));
            }
        }
        else {
            // There is no immediate next contract, so we emit an "end" event for the current contract if it ends in range
            if (curr.end && inRange(curr.inclusiveEnd)) {
                changes.push({
                    type: "end",
                    date: curr.inclusiveEnd,
                    contractA: curr,
                    fields: FIELDS.map(({ label, prop, formatValue, hideOnAccountingReport }) => ({
                        hideOnAccountingReport,
                        label,
                        before: Array.isArray(prop)
                            ? formatValue(company, ...prop.map((p) => curr[p]))
                            : formatValue(company, curr[prop]),
                    })),
                });
            }
            // If there is a next contract and it starts in range, emit a "start" event
            if (next && inRange(next.start)) {
                changes.push(startEvent(next));
            }
        }
    }
    return changes;
}
//# sourceMappingURL=contract.js.map